// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId, LinkModel, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ArrowForward from "@store/Library/Components/Toolkit/Icons/ArrowForward";
import ArrowiOSForward from "@store/Library/Components/Toolkit/Icons/ArrowiOSForward";
import Img, { ImgProps } from "@store/MobiusOverrides/Img/Img";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import { linkStyles, linkTranslatableIcon } from "@store/Themes/common/mixins/link";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { iconScalableStyles } from "@toolkit/Themes/common/mixins/icon";
import * as React from "react";
import { FC, useMemo } from "react";
import { css } from "styled-components";

const enum fields {
    title = "title",
    displayDynamicTitle = "displayDynamicTitle",
    linksWrapperWidth = "linksWrapperWidth",
    titleLink = "titleLink",
    links = "links",
    imageUrl = "imageUrl",
    narrowStyle = "narrowStyle",
    testSelectorId = "testSelectorId",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.displayDynamicTitle]: boolean;
        [fields.linksWrapperWidth]: number;
        [fields.titleLink]: LinkFieldValue;
        [fields.links]: (LinkModel & {
            fields: {
                [fields.imageUrl]: string;
            };
        })[];
        [fields.narrowStyle]: boolean;
        [fields.testSelectorId]: string;
    };
}

export interface StyledLinkListStyles {
    wrapper?: InjectableCss;
    linkListWrapper?: InjectableCss;
    prefixTitle?: TypographyPresentationProps;
    title?: TypographyPresentationProps;
    linksWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    linkImg?: ImgProps;
    linkTextArrow?: InjectableCss;
    linkTextArrowIcon?: IconPresentationProps;
    linkWrapper?: InjectableCss;
    styledLine?: InjectableCss;
}

export const styledLinkListStyles: StyledLinkListStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
        `,
    },
    prefixTitle: {
        css: css`
            font-weight: ${x => x.theme.design.typography.weight.bold};
            margin-right: ${pxToRem(10)};
        `,
    },
    title: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.largest};
            text-align: center;
            width: 100%;

            ${breakpoint("sm")} {
                margin-bottom: ${pxToRem(30)};
            }

            ${breakpoint("md")} {
                margin-bottom: ${pxToRem(40)};
            }

            ${breakpoint("lg")} {
                margin-bottom: ${pxToRem(60)};
            }
        `,
    },
    linksWrapper: {
        css: css`
            display: grid;

            ${breakpoint("xs", "down")} {
                grid-template-columns: 1fr;
            }
        `,
    },
    link: {
        css: css`
            justify-content: center;
            text-align: center;
            width: 100%;
            height: 100%;
            ${linkStyles("text", "text", false, false)}
            ${linkTranslatableIcon(true)}

            ${breakpoint("xs")} {
                padding: ${pxToRem(20, 0)};
            }

            ${breakpoint("sm")} {
                padding: ${pxToRem(10)};
            }

            ${breakpoint("mg")} {
                padding: ${pxToRem(15)};
            }

            ${breakpoint("lg")} {
                padding: ${pxToRem(20)};
            }

            [class^="IconWrapper"] {
                position: relative;
                left: ${pxToRem(10)};

                ${breakpoint("sm")} {
                    left: 0;
                }

                ${breakpoint("md")} {
                    left: ${pxToRem(10)};
                }
            }
        `,
        typographyProps: {
            css: css`
                display: flex;
                flex-direction: column;
                align-items: center;
                height: 100%;
                font-size: ${({ theme }) => theme.design.typography.size.larger};

                ${breakpoint("sm")} {
                    font-size: ${({ theme }) => theme.design.typography.size.base};
                }

                ${breakpoint("md")} {
                    font-size: ${({ theme }) => theme.design.typography.size.larger};
                }
            `,
        },
    },
    linkImg: {
        css: css`
            width: 100%;
            max-width: ${pxToRem(140)};
            margin-bottom: ${pxToRem(32)};
            height: auto;

            ${breakpoint("xs", "down")} {
                margin-bottom: ${pxToRem(10)};
            }
        `,
    },
    linkTextArrow: {
        css: css`
            display: flex;
            align-items: center;
            height: 100%;
        `,
    },
    linkTextArrowIcon: {
        src: ArrowForward,
    },
    linkWrapper: {
        css: css`
            display: none;

            ${breakpoint("xs")} {
                border-bottom: ${pxToRem(1)} solid ${getColor("common.border")};
                border-left: none;

                &:last-child {
                    border-bottom: none;
                }

                &:has(a) {
                    display: list-item;
                }
            }

            ${breakpoint("xs", "only")} {
                border-top: ${pxToRem(1)} solid ${getColor("common.border")};
            }

            ${breakpoint("sm")} {
                border-bottom: none;
                border-left: ${pxToRem(1)} solid ${getColor("common.border")};

                &.hasLink:not(:has(~ .hasLink)) {
                    border-right: ${pxToRem(1)} solid ${getColor("common.border")};
                }
            }
        `,
    },
};

export const styledLinkListNarrowStyles: StyledLinkListStyles = {
    wrapper: {
        css: css`
            max-width: ${pxToRem(1090)};
            margin: 0 auto;

            ${breakpoint("md")} {
                width: 85%;
            }
        `,
    },
    styledLine: {
        css: css`
            width: ${pxToRem(60)};
            height: ${pxToRem(8)};
            background-color: ${getColor("secondary.main")};
            margin-bottom: ${pxToRem(24)};

            ${breakpoint("md")} {
                margin-bottom: ${pxToRem(16)};
            }
        `,
    },
    prefixTitle: {
        css: css`
            display: block;
            font-size: ${pxToRem(30)};
            margin-bottom: ${pxToRem(8)};

            &:last-child {
                margin-bottom: 0;
            }

            ${breakpoint("md")} {
                font-size: ${pxToRem(40)};
                margin-bottom: ${pxToRem(9)};
            }
        `,
    },
    title: {
        forwardAs: "h2",
        css: css`
            font-size: ${x => x.theme.design.typography.size.base};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            line-height: 1.2;
            margin-bottom: ${pxToRem(33)};

            &:last-child {
                margin-bottom: 0;
            }

            ${breakpoint("md")} {
                font-size: ${x => x.theme.design.typography.size.large};
                line-height: 1.1;
            }
        `,
    },
    linksWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: ${pxToRem(12)};

            ${breakpoint("md")} {
                gap: ${pxToRem(18)};
            }
        `,
    },
    linkWrapper: {
        css: css`
            flex-grow: 1;
            flex-basis: 100%;

            ${breakpoint(480)} {
                flex-basis: 48%;
            }

            ${breakpoint("md")} {
                flex-basis: 31%;
            }
        `,
    },
    link: {
        css: css`
            display: flex;
            padding: ${pxToRem(10)};
            background-color: ${getColor("common.background")};
            border-radius: ${pxToRem(10)};
            min-height: ${pxToRem(68)};

            && {
                ${linkStyles("text", "tertiary", false, false)}
            }

            &:hover,
            &:active {
                .arrow {
                    opacity: 1;
                    transform: translateX(0);
                }
            }

            ${breakpoint(480)} {
                min-height: ${pxToRem(78)};
            }

            ${breakpoint("md")} {
                padding: ${pxToRem(12, 24)};
            }
        `,
        typographyProps: {
            css: css`
                display: flex;
                gap: ${pxToRem(10)};
                width: 100%;
                align-items: center;

                ${breakpoint("md")} {
                    gap: ${pxToRem(12)};
                }
            `,
        },
    },
    linkImg: {
        wrapperCss: css`
            flex-shrink: 0;
        `,
        css: css`
            width: ${pxToRem(42)};
            height: auto;
        `,
    },
    linkTextArrow: {
        css: css`
            display: flex;
            align-items: center;
            flex-grow: 1;
            justify-content: space-between;
            gap: ${pxToRem(12)};
        `,
    },
    linkTextArrowIcon: {
        src: ArrowiOSForward,
        css: css`
            ${iconScalableStyles(pxToRem(24), { vertical: 20, horizontal: 32 })}
            flex-shrink: 0;
            opacity: 0;
            transform: translateX(-2px);
            transition: ${transitionHoverStyles("opacity", "transform")};
        `,
    },
};

export const StyledLinkList: FC<OwnProps> = ({
    fields: { narrowStyle: isNarrowStyles, linksWrapperWidth, links: rawLinks, title, displayDynamicTitle },
}) => {
    const { links } = useGetLinks(rawLinks, o => o.fields.destination);
    const userFirstName = useAppSelector(state => {
        const { firstName, userName } = state.context.session;
        return firstName || userName;
    });

    const styles = useMemo(() => {
        if (isNarrowStyles) {
            return styledLinkListNarrowStyles;
        }

        return mergeProps<StyledLinkListStyles>(styledLinkListStyles, {
            linksWrapper: {
                css: css`
                    grid-template-columns: repeat(${links.length}, 1fr);

                    ${breakpoint("md")} {
                        width: ${linksWrapperWidth}%;
                    }
                `,
            },
        });
    }, [links.length, linksWrapperWidth, isNarrowStyles]);

    const prefixTitle = translate("Welcome {name}").replace("{name}", userFirstName);

    return (
        <StyledWrapper {...styles.wrapper}>
            {Boolean(title || displayDynamicTitle) && (
                <Typography {...styles.title}>
                    {isNarrowStyles && <StyledWrapper {...styles.styledLine} />}
                    {displayDynamicTitle && <Typography {...styles.prefixTitle}>{prefixTitle}</Typography>}
                    {Boolean(title) && <span>{title}</span>}
                </Typography>
            )}
            {Boolean(links?.length) && (
                <StyledWrapper as="ul" {...styles.linksWrapper} data-test-selector={fields.testSelectorId}>
                    {links.map((link, index) => {
                        const { openInNewWindow, imageUrl, overriddenTitle } = rawLinks[index].fields;
                        const linkTitle = overriddenTitle || link.title;

                        return (
                            <StyledWrapper
                                {...styles.linkWrapper}
                                as="li"
                                key={index}
                                className={link.url ? "hasLink" : undefined}
                            >
                                {link.url ? (
                                    <Link
                                        {...styles.link}
                                        href={link.url}
                                        target={openInNewWindow ? "_blank" : undefined}
                                    >
                                        {Boolean(imageUrl) && (
                                            <Img {...styles.linkImg} altText={linkTitle} src={imageUrl} />
                                        )}
                                        <StyledWrapper as="span" {...styles.linkTextArrow}>
                                            {linkTitle}
                                            <Icon {...styles.linkTextArrowIcon} className="arrow" />
                                        </StyledWrapper>
                                    </Link>
                                ) : (
                                    <StyledWrapper />
                                )}
                            </StyledWrapper>
                        );
                    })}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: StyledLinkList,
    definition: {
        displayName: "Styled Links List",
        group: "Basic",
        icon: "ax-link-list",
        fieldDefinitions: [
            {
                name: fields.narrowStyle,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                variant: "toggle",
            },
            {
                name: fields.displayDynamicTitle,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.testSelectorId,
                displayName: "Test Selector ID",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.linksWrapperWidth,
                editorTemplate: "DropDownField",
                tooltip: "Change overall width of the links wrapper to get the wanted visual.",
                fieldType: "Contextual",
                options: [
                    {
                        displayName: "40%",
                        value: 40,
                    },
                    {
                        displayName: "50%",
                        value: 50,
                    },
                    {
                        displayName: "60%",
                        value: 60,
                    },
                    {
                        displayName: "80%",
                        value: 80,
                    },
                    {
                        displayName: "90%",
                        value: 90,
                    },
                    {
                        displayName: "100%",
                        value: 100,
                    },
                ],
                defaultValue: 80,
                isVisible: x => !x.fields.narrowStyle,
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                tooltip: "We recommend to use a maximum of 4 links. If you want more links, add another widget.",
                getDisplay: (item: HasFields, state) => {
                    const {
                        destination: { type, value },
                        overriddenTitle,
                    } = item.fields;
                    if (overriddenTitle) {
                        return overriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        tooltip: "If no link is provided, an empty block will be displayed.",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.imageUrl,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
